import { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { PageHead, Card, Pill, Btn, Loading, Err, tableStyles, EmptyState, TextInput, Drawer } from "./_shared";

export default function Plans() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/api/admin/plans").then((d) => setItems(d.items)).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  if (err) return <Err msg={err} />;
  if (!items) return <Loading />;

  return (
    <div data-testid="admin-plans">
      <PageHead title="Plans & pricing" sub={`${items.length} plans configured`} />
      {items.length === 0 ? (
        <EmptyState title="No plans seeded yet" description="Plans are seeded on backend startup. Make sure the seed step succeeded." />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {items.map((p) => (
            <Card key={p.key || p.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--pl-aqua)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>
                    {p.key}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>{p.name}</div>
                </div>
                <Pill tone={p.enabled === false ? "muted" : "lime"}>{p.enabled === false ? "disabled" : "live"}</Pill>
              </div>
              <div style={{ fontSize: 30, fontWeight: 900, marginTop: 14 }}>
                ${p.monthly_price ?? 0}
                <span style={{ fontSize: 12, color: "var(--pl-text-muted)", fontWeight: 600, marginLeft: 4 }}>/ mo</span>
              </div>
              <div style={{ marginTop: 16, display: "grid", gap: 6, fontSize: 12 }}>
                <Stat k="Credits / month" v={(p.included_credits ?? 0).toLocaleString()} />
                <Stat k="Users" v={p.included_users ?? "—"} />
                <Stat k="Projects" v={p.included_projects ?? "—"} />
                <Stat k="Modules" v={(p.enabled_modules || []).length} />
              </div>
              <div style={{ marginTop: 16 }}>
                <Btn testId={`plan-edit-${p.key}`} onClick={() => setEdit(p)} tone="default" style={{ width: "100%" }}>Edit plan</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Drawer open={!!edit} onClose={() => setEdit(null)} title={edit ? `Edit · ${edit.name}` : ""}>
        {edit && <EditPlanForm plan={edit} onSaved={() => { setEdit(null); load(); }} />}
      </Drawer>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed var(--pl-border)" }}>
      <span style={{ color: "var(--pl-text-muted)" }}>{k}</span>
      <span style={{ fontWeight: 700, fontFamily: "JetBrains Mono, monospace" }}>{v}</span>
    </div>
  );
}

function EditPlanForm({ plan, onSaved }) {
  const [form, setForm] = useState({
    name: plan.name || "",
    monthly_price: plan.monthly_price ?? 0,
    included_credits: plan.included_credits ?? 0,
    included_users: plan.included_users ?? 1,
    included_projects: plan.included_projects ?? 1,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/api/admin/plans/${plan.key}`, {
        name: form.name,
        monthly_price: Number(form.monthly_price),
        included_credits: Number(form.included_credits),
        included_users: Number(form.included_users),
        included_projects: Number(form.included_projects),
      });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {["name", "monthly_price", "included_credits", "included_users", "included_projects"].map((k) => (
        <label key={k} style={{ display: "block" }}>
          <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>
            {k.replace(/_/g, " ")}
          </div>
          <TextInput value={form[k]} onChange={(v) => setForm({ ...form, [k]: v })}
            data-testid={`plan-edit-${k}`} style={{ width: "100%" }} />
        </label>
      ))}
      {err && <div style={{ color: "#fda4af", fontSize: 12 }}>{err}</div>}
      <Btn testId="plan-edit-save" tone="primary" onClick={save} disabled={busy}>
        {busy ? "Saving…" : "Save changes"}
      </Btn>
    </div>
  );
}
